import { Navigate, useParams, useSearchParams } from "react-router-dom";
import type { SluggedId } from "Models";
import { AdKitKey } from "types/enums";
import { Spinner } from "@/components/ui/spinner";
import { useGetCurrentLocalFacilityId } from "modules/facility/facilityHooks";
import { useGetAdKitQuery } from "../../api/endpoints/adkits";
import { usePlacementCatalog } from "../../placementCatalog";
import { NewCampaignPage } from "./NewCampaignPage";

/**
 * `/ad-spaces/:campaignId/edit` (CLUB-2657): setup steps 2 and 3, filled in
 * from the campaign. `?placement=` opens step 3 at that placement. Setup's
 * draft is seeded once, so it mounts only after the campaign and the
 * placement catalog have both loaded. An archived campaign can't be edited
 * (it's restored first), so its URL goes back to the campaign page.
 */
export function EditCampaignPage() {
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = useGetCurrentLocalFacilityId();
  const { data: campaign, isError } = useGetAdKitQuery(
    { facilityId, adKitId: (campaignId ?? "") as SluggedId },
    { skip: !facilityId || !campaignId },
  );
  const { placements, isLoading: isCatalogLoading } = usePlacementCatalog();

  if (isError) {
    return (
      <p className="mx-auto max-w-6xl px-4 pt-10 text-center text-sm text-text-muted md:px-6">
        This campaign couldn&rsquo;t be loaded.
      </p>
    );
  }
  if (!campaign || isCatalogLoading || placements.length === 0) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <Spinner className="size-6 text-text-muted" />
      </div>
    );
  }

  if (campaign.deleted) {
    return <Navigate to={`/ad-spaces/${campaign.id}`} replace />;
  }

  const placement = Number(searchParams.get("placement"));
  const startAt = Object.values(AdKitKey).includes(placement)
    ? placement
    : undefined;

  return <NewCampaignPage key={campaign.id} edit={{ campaign, startAt }} />;
}
